import React, { useState, useEffect } from 'react';
import { Modal, Spinner, ErrorBanner, SearchableSelect } from '../../components/ui';
import { errMsg, fmt } from '../../utils/format';
import { useFetch } from '../../hooks/useFetch';
import api from '../../utils/api';

export default function BOMDetailModal({ bomId, onClose, onSaved }) {
  const { data: bom, loading, error } = useFetch(`/api/bom/${bomId}`);
  const { data: materials } = useFetch('/api/raw-materials');

  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(true);

  useEffect(() => {
    if (bom?.rawMaterials) {
      setRows(bom.rawMaterials.map(r => ({
        materialId: String(r.material_id),
        qtyPerUnit: String(r.qty_per_unit),
        // Display-only
        materialName: r.material_name,
        unitOfMeasure: r.unit_of_measure,
        unitCost: r.unit_cost,
        quantityOnHand: r.quantity_on_hand,
        isProductSource: r.is_product_source,
      })));
    }
  }, [bom]);

  const setRow = (idx, k, v) => setRows(r => r.map((row, j) => j === idx ? { ...row, [k]: v } : row));
  const addRow = () => setRows(r => [...r, { materialId: '', qtyPerUnit: '1' }]);
  const removeRow = (idx) => setRows(r => r.filter((_, j) => j !== idx));

  const materialOptions = (materials || []).map(m => ({ value: m.id, label: `${m.name} (${m.unit_of_measure})` }));

  const totalCost = rows.reduce((s, r) => {
    const m = (materials || []).find(m => String(m.id) === String(r.materialId));
    const cost = m ? parseFloat(m.unit_cost) : parseFloat(r.unitCost || 0);
    return s + (parseFloat(r.qtyPerUnit) || 0) * (cost || 0);
  }, 0);

  const save = async () => {
    const valid = rows.filter(r => r.materialId && r.materialId !== '' && r.qtyPerUnit && parseFloat(r.qtyPerUnit) > 0);
    if (!valid.length) { alert('Add at least one part'); return; }
    setSaving(true);
    try {
      await api.put(`/api/bom/${bomId}/rawMaterials`, {
        rawMaterials: valid.map(r => ({
          materialId: parseInt(r.materialId) || null,
          qtyPerUnit: parseFloat(r.qtyPerUnit),
          preferredVendorId: null,
        })),
      });
      // Remove any empty/incomplete rows after saving
      setRows(rows.filter(r => r.materialId && r.materialId !== ''));
      setEditing(false);
      onSaved && onSaved('BOM updated successfully');
      onClose();
    } catch (err) { alert(errMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={bom ? `BOM — ${bom.sku} (${bom.product_name})` : 'Bill of Materials'} onClose={onClose} wide
      footer={
        editing
          ? <div style={{ display:'flex', justifyContent:'space-between', width:'100%' }}>
              <button className="btn btn-danger btn-sm" onClick={async () => {
                if (!window.confirm('Delete this BOM? This cannot be undone.')) return;
                try { await api.delete(`/api/bom/${bomId}`); onSaved && onSaved('BOM deleted'); onClose(); }
                catch (err) { alert(errMsg(err)); }
              }}><i className="ti ti-trash" /> Delete BOM</button>
              <div style={{ display:'flex', gap:8 }}>
                <button className="btn" onClick={onClose}>Cancel</button>
                <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
              </div>
            </div>
          : <div style={{ display:'flex', justifyContent:'space-between', width:'100%' }}>
              <button className="btn btn-danger btn-sm" onClick={async () => {
                if (!window.confirm('Delete this BOM? This cannot be undone.')) return;
                try { await api.delete(`/api/bom/${bomId}`); onSaved && onSaved('BOM deleted'); onClose(); }
                catch (err) { alert(errMsg(err)); }
              }}><i className="ti ti-trash" /> Delete BOM</button>
              <div style={{ display:'flex', gap:8 }}>
                <button className="btn" onClick={onClose}>Close</button>
                <button className="btn btn-primary" onClick={() => setEditing(true)}><i className="ti ti-pencil" /> Edit</button>
              </div>
            </div>
      }>
      {loading && <Spinner />}
      {error && <ErrorBanner message={error} />}
      {bom && (
        <div>
          <div style={{ display: 'flex', gap: 24, marginBottom: 16, fontSize: 13 }}>
            <div><span style={{ color: 'var(--text-tertiary)' }}>Parts:</span> <strong>{rows.length}</strong></div>
            <div><span style={{ color: 'var(--text-tertiary)' }}>Material cost / unit:</span> <strong>{fmt.currency(totalCost)}</strong></div>
            <div><span style={{ color: 'var(--text-tertiary)' }}>Last updated:</span> <strong>{fmt.date(bom.updated_at)}</strong></div>
          </div>

          {!editing ? (
            <table style={{ width: '100%' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, color: 'var(--text-tertiary)' }}>Part</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, color: 'var(--text-tertiary)' }}>Type</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: 11, color: 'var(--text-tertiary)' }}>Qty per unit</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, color: 'var(--text-tertiary)' }}>UoM</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: 11, color: 'var(--text-tertiary)' }}>On hand</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: 11, color: 'var(--text-tertiary)' }}>Line cost</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const lineCost = (parseFloat(r.qtyPerUnit) || 0) * (parseFloat(r.unitCost) || 0);
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 500 }}>{r.materialName}</td>
                      <td style={{ padding: '6px 8px' }}>
                        {r.isProductSource
                          ? <span className="badge badge-blue" style={{ fontSize: 10 }}>Finished good</span>
                          : <span className="badge badge-gray" style={{ fontSize: 10 }}>Raw material</span>}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{r.qtyPerUnit}</td>
                      <td style={{ padding: '6px 8px' }}>{r.unitOfMeasure}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt.number(r.quantityOnHand)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt.currency(lineCost)}</td>
                    </tr>
                  );
                })}
                {!rows.length && (
                  <tr><td colSpan={6} style={{ padding: '16px 8px', textAlign: 'center', color: 'var(--text-tertiary)' }}>No parts in this BOM</td></tr>
                )}
              </tbody>
            </table>
          ) : (
            <div>
              {rows.map((row, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'end' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    {idx === 0 && <label style={{ fontSize: 11 }}>Part</label>}
                    <SearchableSelect
                      value={row.materialId}
                      onChange={v => setRow(idx, 'materialId', v)}
                      options={materialOptions}
                      placeholder="— Search parts —"
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    {idx === 0 && <label style={{ fontSize: 11 }}>Qty per unit</label>}
                    <input type="number" step="0.0001" min="0" value={row.qtyPerUnit}
                      onChange={e => setRow(idx, 'qtyPerUnit', e.target.value)} />
                  </div>
                  <button type="button" className="btn btn-sm btn-danger"
                    style={{ marginTop: idx === 0 ? 18 : 0 }} onClick={() => removeRow(idx)}>
                    <i className="ti ti-x" />
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-sm" onClick={addRow} style={{ marginTop: 4 }}>
                <i className="ti ti-plus" /> Add part
              </button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
